import React, { useState, useEffect } from 'react';
import socketIOClient from 'socket.io-client';
import { connect } from 'react-redux';
import { toastr } from 'react-redux-toastr';
import { 
    Container, 
    Typography, 
    Button, 
    Box, 
    Paper,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from '@mui/material';
import { Close as CloseIcon, Phone as PhoneIcon, PhoneDisabled as PhoneDisabledIcon } from '@mui/icons-material';

import {
    cancelCallRequest,
    cancelCallRequestSuccess,
    finishCallRequest,
    removePollingInterval,
} from '../actions/callRequestActions';

import StoreHeader from './StoreHeader';
import { fetchStores } from '../actions/storeActions';
import { fetchCallByCallRequest } from '../actions/callActions';
import { useNavigate } from 'react-router-dom';

const WaitingRoom = ({
    cancelCallRequest,
    callRequest,
    store,
    hasCall,
    fetchStores,
    cancelCallRequestSuccess,
    fetchCallByCallRequest,
    finishCallRequest,
    removePollingInterval,
}) => {
    const navigate = useNavigate();
    const [queue, setQueue] = useState([]);
    const [showCallModal, setShowCallModal] = useState(false);

    // Request notification permission on mount
    useEffect(() => {
        if ('Notification' in window && Notification.permission === 'default') {
            Notification.requestPermission();
        }
    }, []);

    const submitCancelCallRequest = () => {
        const { storeId, callRequestId } = callRequest.callRequest;
        cancelCallRequest(storeId, callRequestId);
    };

    const handleEnterCall = () => {
        setShowCallModal(false);
        navigate('/call');
    };

    const handleLeaveCall = () => {
        const { storeId, callRequestId } = callRequest.callRequest;
        setShowCallModal(false);
        finishCallRequest(storeId, callRequestId);
    };

    const getPosition = () => {
        if (callRequest && callRequest.callRequest.callRequestId) {
            return queue.indexOf(callRequest.callRequest.callRequestId.toString()) + 1;
        } else {
            return 0;
        }
    };

    // Socket EFFECT
    useEffect(() => {
        // No loguear el token del pedido de llamada: autoriza a operar sobre él.
        
        const socket = socketIOClient(
            `${process.env.REACT_APP_API_BASE_URL}?storeId=${callRequest.callRequest.storeId}`,
            {
                path: '/waiting-room-socket',
                cookie: false,
                extraHeaders: {
                    Authorization: `Bearer ${callRequest.callRequest.token}`,
                },
                transportOptions: {
                    polling: {
                        extraHeaders: {
                            Authorization: `Bearer ${callRequest.callRequest.token}`,
                        },
                    },
                },
            }
        );

        socket.on('connect', () => {
            console.log('::: WaitingRoom SOCKET CONNECTED');
        });

        socket.on('disconnect', () => {
            console.log('::: WaitingRoom SOCKET DISCONNECTED');
        });

        socket.on('connect_error', (error) => {
            console.error('::: WaitingRoom SOCKET CONNECTION ERROR:', error);
        });

        socket.on('WAITING_ROOM_SENDED', function (waitingRoom) {
            console.log('::: WaitingRoom WAITING_ROOM_SENDED', waitingRoom);
            setQueue(waitingRoom.queue);
        });

        socket.on('QUEUE_CHANGED', function (queue) {
            console.log('::: WaitingRoom QUEUE_CHANGED', queue);
            setQueue(queue);
        });

        return () => {
            console.log("::: WaitingRoom DISCONNECT SOCKET");
            socket.disconnect();
        };
    }, [callRequest.callRequest.storeId, callRequest.callRequest.token]);

    // Check no callRequest EFFECT
    useEffect(() => {
        if (!callRequest) {
            navigate('/home');
        }
    }, [callRequest, navigate]);

    // Check if user has been called EFFECT
    useEffect(() => {
        if (hasCall) {
            // Stop polling when user is called
            removePollingInterval();
            
            // Play notification sound
            try {
                const notificationSound = new Audio('/notification_sound.mp3');
                notificationSound.volume = 0.5;
                notificationSound.play().catch(error => {
                    console.log('Could not play notification sound:', error);
                });
            } catch (error) {
                console.log('Error creating audio:', error);
            }
            
            // Show browser notification
            if ('Notification' in window && Notification.permission === 'granted') {
                try {
                    const notification = new Notification('¡Es tu turno!', {
                        body: 'Has sido llamado para tu videollamada. Haz clic para responder.',
                        icon: '/videocallshop-logo.png',
                        badge: '/videocallshop-logo.png',
                        requireInteraction: true,
                        tag: 'call-notification'
                    });
                    
                    // Focus window when notification is clicked
                    notification.onclick = () => {
                        window.focus();
                        notification.close();
                    };
                } catch (error) {
                    console.log('Error showing notification:', error);
                }
            }
            
            setShowCallModal(true);
        }
    }, [hasCall, removePollingInterval]);

    // Check store info EFFECT
    useEffect(() => {
        if(!store) {
            fetchStores();
        }
    }, [store, fetchStores]);

    // Check State EFFECT
    useEffect(() => {        
        console.log('::: WaitingRoom CHECK STATE EFFECT', callRequest.callRequest.state);

        switch(callRequest.callRequest.state) {
            case 'PROCESSING_CALL':
                toastr.info('Info', 'Su llamada está siendo procesada.');
                console.log('::: WaitingRoom PROCESSING CALL');
                break;
                
            case 'CALLED':
                toastr.info('Info', 'Ha sido llamado, aguarde un instante.');
                fetchCallByCallRequest(callRequest.callRequest);
                break;

            case 'CANCELLED':
                toastr.info('Info', 'Su llamada fue cancelada.');
                cancelCallRequestSuccess();
                break;
                
            default:
                // Handle any other states if needed
                break;
        }
    }, [callRequest, fetchCallByCallRequest, cancelCallRequestSuccess]);

    return (
        <Container maxWidth="md" sx={{ py: 4 }}>
            <StoreHeader store={store} />
            
            <Paper elevation={3} sx={{ p: 4, mt: 3, textAlign: 'center' }}>
                <Typography variant="h4" component="h1" gutterBottom>
                    Usted se encuentra en la fila para ser atendido
                </Typography>
                
                <Typography variant="h5" component="h2" gutterBottom>
                    Su posición es
                </Typography>
                
                <Box sx={{ 
                    fontSize: '8rem', 
                    fontWeight: 'bold', 
                    color: 'primary.main',
                    my: 3,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                }}>
                    {getPosition()}
                </Box>
                
                {callRequest.isFetching && !hasCall && (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                        <CircularProgress size={20} />
                        <Typography variant="body1" color="text.secondary">
                            Verificando estado...
                        </Typography>
                    </Box>
                )}
            </Paper>
            
            <Box sx={{ 
                position: 'fixed', 
                bottom: 40, 
                right: 40,
                zIndex: 1000
            }}>
                <Button
                    variant="contained"
                    color="warning"
                    startIcon={<CloseIcon />}
                    onClick={submitCancelCallRequest}
                    size="large"
                    sx={{ borderRadius: 2 }}
                >
                    Salir de la fila
                </Button>
            </Box>

            <Dialog 
                open={showCallModal} 
                onClose={() => {}} 
                maxWidth="sm" 
                fullWidth
                disableEscapeKeyDown
            >
                <DialogTitle sx={{ textAlign: 'center', fontSize: '1.5rem', fontWeight: 'bold' }}>
                    ¡Es tu turno!
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body1" sx={{ textAlign: 'center', mb: 2 }}>
                        Has sido llamado para iniciar tu videollamada.
                    </Typography>
                    <Typography variant="body2" sx={{ textAlign: 'center', color: 'text.secondary' }}>
                        ¿Deseas ingresar a la llamada?
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'center', gap: 2, pb: 3 }}>
                    <Button
                        variant="outlined"
                        color="error"
                        startIcon={<PhoneDisabledIcon />}
                        onClick={handleLeaveCall}
                        size="large"
                    >
                        Rechazar
                    </Button>
                    <Button
                        variant="contained"
                        color="success"
                        startIcon={<PhoneIcon />}
                        onClick={handleEnterCall}
                        size="large"
                        autoFocus
                    >
                        Ingresar
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

const mapStateToProps = (state) => {
    return {
        callRequest: state.callRequest,
        store: state.stores.stores[state.callRequest.callRequest.storeId],
        hasCall: !!state.call.call,
    };
};

export default connect(
    mapStateToProps,
    {
        cancelCallRequest,
        fetchCallByCallRequest,
        cancelCallRequestSuccess,
        fetchStores,
        finishCallRequest,
        removePollingInterval
    }
)(WaitingRoom);
